import CombinedRange from 'common/filters/combined-range';
import InputFilter from 'common/filters/input-filter';
import PeopleFilter from 'common/filters/people-filter';
import RangeFilter from 'common/filters/range-filter';
import ValueFilter from 'common/filters/value-filter';
import cloneDeep from 'lodash.clonedeep';
import PropTypes from 'prop-types';
import React, { forwardRef, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import EntityFilter from '../../common/filters/entity-filter/containers/entity-filter-container';
import GeoFilter from '../../common/filters/geo-filter/components/geo-filter';
import OperatorFilter from '../../common/filters/operator-filter';
import { searchOperators } from '../../utils/app-configs';
import { determineOperatorAndValue, transformFiltersToUrlParams } from '../../utils/helpers';
import { updateFilters } from '../../worklist/containers/worklist-actions';

const ColumnFilter = styled.div`
  padding: 10px;
  ${p => p.isEntity && 'height: 220px;'}
`;

const WorkGridFilters = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const activeFilters = useSelector(state => state.worklist.activeFilters);
  const filterDefinitions = useSelector(state => state.worklist.filterDefinitions);

  // expose AG Grid Filter Lifecycle callbacks
  useImperativeHandle(ref, () => ({
    doesFilterPass() {
      return true;
    },

    isFilterActive() {
      return activeFilters && activeFilters[props.colDef.filterId]?.length;
    },

    // We aren't using getModel() and setModel()
    // otherwise this would not work with the filtersidepanel,
    // so safe to just leave these empty.
    // eslint-disable-next-line no-empty-function
    getModel() { },

    // eslint-disable-next-line no-empty-function
    setModel() { },
  }), [activeFilters]);

  const onRangeFilterChange = ({ filter, values }) => {
    const filters = { ...activeFilters, [filter]: values };
    dispatch(updateFilters(filters));
  };

  const onEntityFilterChange = ({ filter, value }) => {
    if (!value?.length) {
      const newActiveFilters = cloneDeep(activeFilters);
      delete newActiveFilters[filter];
      dispatch(updateFilters({ ...newActiveFilters }));
    } else {
      const filters = { ...activeFilters, [filter]: [...value] };
      dispatch(updateFilters(filters));
    }
  };

  const onValueFilterChange = ({ filter, value, checked, operator }, isBulk, selectedValues) => {
    let filters = { ...activeFilters };
    // if there is no active filter for this valueFilter, create one.
    if (isBulk) {
      filters = { ...filters, [filter]: selectedValues };
    } else {
      // if there is no active filter for this valueFilter, create one.
      if (!filters[filter]) {
        filters = { ...filters, [filter]: [] };
      }
      // When there is a operator you will receive data with the short hand
      // first we have to check what the operator and value is.Then we reformat it for later use
      let currentFilters = filters[filter];
      if (operator && filters[filter]?.length) {
        const determinedOperatorAndValue = determineOperatorAndValue(filters[filter].join(';'));
        currentFilters = [determinedOperatorAndValue.value];
      }

      // if the filter value is checked
      if (checked) {
        // add an extra filer value.
        filters = { ...filters, [filter]: [...currentFilters, value] };
      } else {
        // remove the filter value
        let newValues = currentFilters?.filter(v => v !== value);
        if (operator) {
          newValues = currentFilters[0].split(';')?.filter(v => v !== value);
        }
        filters = { ...filters, [filter]: newValues };
      }

      // When you are done with adding/removing items.
      // You have to reapply the short hand on the filter that has an operator.
      if (operator && filters[filter]?.length) {
        let operatorValue = '';
        operatorValue = filters[filter].join(';');
        operatorValue = operator.shortHand.replace(/{value}/, operatorValue);
        filters = { ...filters, [filter]: [operatorValue] };
      }
    }
    dispatch(updateFilters(filters));
  };

  const onSearchFilterChange = ({ filter, value }) => {
    if (!value || !value[0]) {
      const newActiveFilters = cloneDeep(activeFilters);
      delete newActiveFilters[filter];
      dispatch(updateFilters({ ...newActiveFilters }));
    } else {
      dispatch(updateFilters({ ...activeFilters, [filter]: value }));
    }
  };

  const onOperatorFilterChange = ({ filter, value }) => {
    if (!value || !value[0]) {
      const newActiveFilters = cloneDeep(activeFilters);
      delete newActiveFilters[filter];
      dispatch(updateFilters({ ...newActiveFilters }));
    } else {
      dispatch(updateFilters({ ...activeFilters, [filter]: value }));
    }
  };

  const onGeoFilterChange = ({ filter, value }) => {
    if (!value?.length) {
      const newActiveFilters = cloneDeep(activeFilters);
      delete newActiveFilters[filter];
      dispatch(updateFilters({ ...newActiveFilters }));
    } else {
      dispatch(updateFilters({ ...activeFilters, [filter]: [...value] }));
    }
  };

  const renderFilter = (filter) => {
    if (filter?.isHidden) {
      return null;
    }
    if (filter?.type === 'options' || filter?.type === 'sectionedOptions') {
      return (
        <ValueFilter
          activeFilters={activeFilters[filter.id]}
          description={filter.description}
          options={filter.options}
          operators={filter.operators || []}
          onChange={({ value, checked, operator }, selected) =>
            onValueFilterChange(
              { filter: filter.id, value, checked, operator },
              props.colDef.isDetails && !operator,
              selected,
            )
          }
          hasCategories={filter.type === 'sectionedOptions'}
          showCount={filter.showCount}
          isColumnFilter={true}
          needsConfirm={props.colDef.isDetails}
        />
      );
    }

    if (filter.type === 'geoPolygon') {
      return <GeoFilter
        activeFilters={activeFilters[filter.id]}
        description={filter.description}
        timeRanges={filter.timeRanges}
        options={filter.options}
        addressOptions={filter.addressOptions}
        onChange={value =>
          onGeoFilterChange({ filter: filter.id, value })
        }
      />;
    }

    if (filter?.type === 'combinedRange') {
      return (
        <CombinedRange
          filters={filter}
          relativeTimingRangeOptions={filter.rangeFixPoints}
          activeFilters={activeFilters}
          isColumnFilter={true}
          needsConfirm={props.colDef.isDetails}
          onChange={(filterId, values) =>
            onRangeFilterChange({ filter: filterId, values })
          }
        />
      );
    }

    if (filter?.type === 'range') {
      return (
        <RangeFilter
          description={filter.description}
          min={filter.min}
          max={filter.max}
          unit={filter.unit}
          activeFilters={activeFilters[filter.id]}
          onChange={values => onRangeFilterChange({ filter: filter.id, values })}
          isColumnFilter={true}
          allowClear={true}
          needsConfirm={props.colDef.isDetails}
        />
      );
    }

    if (filter?.type === 'search' || filter?.type === 'freeText') {
      if (filter?.id === 'installedAtPostalCode') {
        return (
          <OperatorFilter
            description={filter.description}
            activeFilters={activeFilters[filter.id]}
            operators={filter.operators}
            onChange={value => onOperatorFilterChange({ filter: filter.id, value })}
            defaultOperator={searchOperators.startsWith.id}
            prefix='pc'
            isColumnFilter={true}
          />
        );
      }
      return (
        <InputFilter
          description={filter.description}
          activeFilters={activeFilters[filter.id]}
          onChange={value => onSearchFilterChange({ filter: filter.id, value })}
          isColumnFilter={true}
          needsConfirm={props.colDef.isDetails}
        />
      );
    }

    if (filter?.type === 'entity' || filter?.type === 'multiEntity') {
      const showChildrenOperator = filter.operators?.length &&
        filter.operators.find(o => o.id === 'showChildren');
      return (
        <EntityFilter
          description={filter.description}
          id={filter.id}
          type={filter.type}
          onChange={value => onEntityFilterChange({
            filter: filter.id,
            value: value ? [value] : [],
          })}
          activeOptions={filter.options}
          multiType={!!filter.types?.length}
          types={filter.types}
          isColumnFilter={true}
          filters={Object.keys(activeFilters)?.length ?
            transformFiltersToUrlParams(activeFilters) :
            ''
          }
          needsConfirm={props.colDef.isDetails}
          worklistId={props.colDef.worklistId}
          fixOptions={props.colDef.fixedFilterValues[filter.id] || []}
          isMultiEntity={filter?.type === 'multiEntity'}
          operators={filter.operators}
          showChildren={activeFilters[filter.id]?.length && showChildrenOperator ?
            // eslint-disable-next-line max-len
            activeFilters[filter.id][0]?.toLowerCase()?.startsWith(showChildrenOperator?.description?.toLowerCase()) :
            false}
          currentFilterValue={activeFilters[filter.id]}
        />
      );
    }

    if (filter?.type === 'people' || filter?.type === 'user') {
      return (
        <PeopleFilter
          description={filter.description}
          activeFilters={activeFilters[filter.id]}
          onChange={value => onSearchFilterChange({ filter: filter.id, value })}
          defaultMe={false}
          currentUserId={filter.userId}
          currentUserName={filter.userDisplayName}
          isColumnFilter={true}
        />
      );
    }

    // This should not happen!!
    return <span>{`Could not render filter ${filter?.id}`}</span>;
  };
  const filter = filterDefinitions?.find(f => f.id === props?.colDef?.filterId);
  return (
    <ColumnFilter isEntity={filter?.type === 'entity'}>
      {filter && renderFilter(filter)}
    </ColumnFilter>
  );
});

WorkGridFilters.propTypes = {
  colDef: PropTypes.object,
};

export default WorkGridFilters;
